import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages which draft-review cell is in edit mode without blur/click races.
 * Pair view cells with `onMouseDown` + `preventDefault` (see ProcessDraftReviewTable).
 */
export function useProcessDraftFieldEditing() {
  const [editingField, setEditingField] = useState(null);
  const suppressBlurStopRef = useRef(false);
  const blurTimerRef = useRef(null);

  const clearBlurTimer = useCallback(() => {
    if (blurTimerRef.current != null) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  const startEditing = useCallback((processId, field) => {
    clearBlurTimer();
    suppressBlurStopRef.current = true;
    setEditingField({ processId, field });
    queueMicrotask(() => {
      suppressBlurStopRef.current = false;
    });
  }, [clearBlurTimer]);

  const stopEditing = useCallback(() => {
    if (suppressBlurStopRef.current) return;
    clearBlurTimer();
    blurTimerRef.current = setTimeout(() => {
      setEditingField(null);
      blurTimerRef.current = null;
    }, 0);
  }, [clearBlurTimer]);

  const clearEditing = useCallback(() => {
    clearBlurTimer();
    suppressBlurStopRef.current = false;
    setEditingField(null);
  }, [clearBlurTimer]);

  const isFieldEditing = useCallback(
    (processId, field) =>
      editingField?.processId === processId && editingField?.field === field,
    [editingField]
  );

  useEffect(() => clearBlurTimer, [clearBlurTimer]);

  return { editingField, startEditing, stopEditing, clearEditing, isFieldEditing };
}
